import { useState } from "react";
import { useAuth } from "./lib/useAuth";
import { confirmLeave } from "./lib/unsaved";
import { Logo, LogoMark } from "./components/ui";
import { Toaster } from "./components/save";
import SignIn from "./features/auth/SignIn";
import RemoteSign from "./features/sign/RemoteSign";
import RemotePay from "./features/pay/RemotePay";
import Home from "./features/home/Home";
import LotList from "./features/lot/LotList";
import AddVehicle from "./features/lot/AddVehicle";
import VehicleDetail from "./features/lot/VehicleDetail";
import CustomerList from "./features/people/CustomerList";
import AddCustomer from "./features/people/AddCustomer";
import CustomerDetail from "./features/people/CustomerDetail";
import Reports from "./features/reports/Reports";
import DealList from "./features/deals/DealList";
import NewDeal from "./features/deals/NewDeal";
import DealDetail from "./features/deals/DealDetail";
import PaymentList from "./features/payments/PaymentList";
import NewCharge from "./features/payments/NewCharge";
import ChargeDetail from "./features/payments/ChargeDetail";
import Settings from "./features/settings/Settings";

type View =
  | { name: "home" }
  | { name: "lot" } | { name: "addVehicle" } | { name: "vehicle"; id: string }
  | { name: "deals" } | { name: "newDeal" } | { name: "deal"; id: string }
  | { name: "people" } | { name: "addCustomer" } | { name: "customer"; id: string }
  | { name: "payments" } | { name: "newCharge" } | { name: "charge"; id: string }
  | { name: "reports" }
  | { name: "settings" };

type Tab = "home" | "lot" | "deals" | "people" | "payments" | "reports" | "settings";
const TAB_OF: Record<View["name"], Tab> = {
  home: "home",
  lot: "lot", addVehicle: "lot", vehicle: "lot",
  deals: "deals", newDeal: "deals", deal: "deals",
  people: "people", addCustomer: "people", customer: "people",
  payments: "payments", newCharge: "payments", charge: "payments",
  reports: "reports",
  settings: "settings",
};

const NAV: { tab: Tab; label: string; icon: string }[] = [
  { tab: "home", label: "Home",
    icon: "M3 8.5 10 3l7 5.5V17a1 1 0 0 1-1 1h-3v-5H7v5H4a1 1 0 0 1-1-1z" },
  { tab: "lot", label: "Vehicles",
    icon: "M3 13.5V17M3 13.5l1.6-4a2 2 0 0 1 1.9-1.3h9a2 2 0 0 1 1.9 1.3l1.6 4M3 13.5h15M18 13.5V17M6 17h.01M15 17h.01" },
  { tab: "deals", label: "Deals",
    icon: "M5 3h7l4 4v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM12 3v4h4M7 12h6M7 15h4" },
  { tab: "people", label: "Customers",
    icon: "M10 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM4 18a6 6 0 0 1 12 0" },
  { tab: "payments", label: "Payments",
    icon: "M2.5 6.5h15a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1zM1.5 9.5h17M4 12.5h3" },
  { tab: "reports", label: "Reports",
    icon: "M4 17V9M9 17V4M14 17v-6M3 17h14" },
  { tab: "settings", label: "Settings",
    icon: "M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM10 2.5v2M10 15.5v2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M2.5 10h2M15.5 10h2M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" },
];

/**
 * Wide layout suits tables, charts and grids; forms read better narrow.
 *
 * A form at full width is worse, not better — a text input two thousand
 * pixels long is harder to fill and a line of prose that wide is harder to
 * read. So New deal, Add a customer and Settings stay in a column.
 *
 * Reports and the vehicle page were on the wrong side of that: reports is
 * nothing but tables and charts, and a vehicle has a photo grid, an expense
 * table and a document list, all of which were being squeezed into 760px
 * while the rest of the monitor sat empty.
 */
const WIDE = new Set(["home", "lot", "deals", "people", "payments", "reports",
                      "vehicle", "deal"]);

export default function App() {
  const { session, loading, signOut } = useAuth();
  /* A stack rather than a single view. Screens are reached from several
     places — a deal from the list, from the dashboard, from a new deal — so
     "back" has to mean where you came from, not a fixed parent. */
  const [stack, setStack] = useState<View[]>([{ name: "home" }]);
  const [refresh, setRefresh] = useState(0);

  /* A customer following a signing link is not staff and has no account.
     This is checked before the auth gate, and before anything else loads, so
     the link never bounces them to a sign-in page they cannot pass. */
  const params = new URLSearchParams(window.location.search);
  const signToken = params.get("sign");
  if (signToken) return <RemoteSign token={signToken} />;

  /* Same reasoning for a payment link: the person following it is a customer,
     not staff, and has no account to be bounced to a sign-in page with. */
  const payToken = params.get("pay");
  if (payToken) return <RemotePay token={payToken} />;

  if (loading) return <div className="boot">Loading</div>;
  if (!session) return <SignIn />;

  const view = stack[stack.length - 1]!;
  const tab = TAB_OF[view.name];

  /* Every screen change goes through here, so this is the one place that can
     ask before walking away from a half-filled form. The browser handles the
     tab being closed; clicking the sidebar is ours to catch. */
  const setView = (v: View) => {
    if (!confirmLeave()) return;
    setStack((s) => [...s, v]);
  };
  const goto = (v: View) => { setRefresh((n) => n + 1); setView(v); };
  /* The back button and the sidebar leave the screen too. They used to skip
     the question, so the one warning that existed could be walked past. */
  const back = () => {
    if (!confirmLeave()) return;
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  };

  /* Tapping a section you are already in resets to it rather than deepening
     the stack, which is what a sidebar is expected to do. */
  const openTab = (t: Tab) => {
    if (stack[stack.length - 1]?.name === t) return;
    if (!confirmLeave()) return;
    setStack((s) => [...s, { name: t } as View]);
  };

  const canGoBack = stack.length > 1;
  const prev = canGoBack ? stack[stack.length - 2]! : null;
  const backLabel = prev
    ? ({ home: "Home", lot: "vehicles", deals: "deals", people: "customers",
         payments: "payments", settings: "settings", vehicle: "the vehicle",
         deal: "the deal", customer: "the customer", addVehicle: "the form",
         addCustomer: "the form", newDeal: "the deal",
         newCharge: "the form", charge: "the payment" } as Record<string, string>)
        [prev.name] ?? "back"
    : "";

  /** Home hands back simple names; map them onto real views. */
  const fromHome = (where: string) => {
    if (where === "addVehicle") return setView({ name: "addVehicle" });
    if (where === "newDeal") return setView({ name: "newDeal" });
    if (where === "customers") return setView({ name: "people" });
    setView({ name: where as View["name"] } as View);
  };

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-top">
          <button className="logo-btn" onClick={() => goto({ name: "home" })}
                  aria-label="Home">
            <Logo width={158} />
            <LogoMark size={30} />
          </button>
        </div>
        <nav className="nav">
          {NAV.map((n) => (
            <button key={n.tab} className={`navitem ${tab === n.tab ? "on" : ""}`}
                    onClick={() => openTab(n.tab)}>
              <svg viewBox="0 0 20 20" width="19" height="19" aria-hidden="true">
                <path d={n.icon} fill="none" stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        <button className="signout"
                onClick={() => { if (confirmLeave()) void signOut(); }}>Sign out</button>
      </aside>

      <main className="main">
        <div className={`content${WIDE.has(view.name) ? " wide" : ""}`}>
          {canGoBack && (
            <button className="backbtn" onClick={back}>
              <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                <path d="M10 3 5 8l5 5" fill="none" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to {backLabel}
            </button>
          )}
          {view.name === "home" && (
            <Home key={refresh} go={fromHome}
                  onOpenDeal={(id) => setView({ name: "deal", id })} />
          )}

          {view.name === "lot" && (
            <LotList key={refresh} onAdd={() => setView({ name: "addVehicle" })}
                     onOpen={(id) => setView({ name: "vehicle", id })} />
          )}
          {view.name === "addVehicle" && (
            <AddVehicle onCancel={() => setView({ name: "lot" })}
                        onDone={() => goto({ name: "lot" })} />
          )}
          {view.name === "vehicle" && (
            <VehicleDetail id={view.id} onBack={() => goto({ name: "lot" })} />
          )}

          {view.name === "deals" && (
            <DealList key={refresh} onAdd={() => setView({ name: "newDeal" })}
                      onOpen={(id) => setView({ name: "deal", id })} />
          )}
          {view.name === "newDeal" && (
            <NewDeal onCancel={() => setView({ name: "deals" })}
                     onDone={(dealId) => goto({ name: "deal", id: dealId })} />
          )}
          {view.name === "deal" && (
            <DealDetail id={view.id} onBack={() => goto({ name: "deals" })}
                        onOpenCustomer={(cid) => setView({ name: "customer", id: cid })} />
          )}

          {view.name === "people" && (
            <CustomerList key={refresh} onAdd={() => setView({ name: "addCustomer" })}
                          onOpen={(id) => setView({ name: "customer", id })} />
          )}
          {view.name === "addCustomer" && (
            <AddCustomer onCancel={() => setView({ name: "people" })}
                         onDone={() => goto({ name: "people" })} />
          )}
          {view.name === "customer" && (
            <CustomerDetail id={view.id} onBack={() => goto({ name: "people" })} />
          )}

          {view.name === "payments" && (
            <PaymentList key={refresh} onAdd={() => setView({ name: "newCharge" })}
                         onOpen={(id) => setView({ name: "charge", id })} />
          )}
          {view.name === "newCharge" && (
            <NewCharge onCancel={() => setView({ name: "payments" })}
                       onDone={(chargeId) => goto({ name: "charge", id: chargeId })} />
          )}
          {view.name === "charge" && (
            <ChargeDetail id={view.id} onBack={() => goto({ name: "payments" })} />
          )}

          {view.name === "reports" && <Reports key={refresh} />}

          {view.name === "settings" && <Settings key={refresh} />}
        </div>
      </main>
      <Toaster />
    </div>
  );
}
